/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jun 26 2024
 **/

import { BaseService, ReturnData } from "@/frameworks/libraries/restservice";

class Conference extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (conferenceId?: string, path?: string) =>
		`/meeting/conferences${conferenceId ? `/${conferenceId}` : ""}${path ? `/${path}` : ""}`;

	public list = (
		parameter?: Meeting.Conference.List.Request
	): Promise<ReturnData<Meeting.Conference.List.Response>> => this.get(this.APIPath(), parameter);

	public create = (
		parameter: Meeting.Conference.Create.Request
	): Promise<ReturnData<Meeting.Conference.Create.Response>> => this.post(this.APIPath(), parameter);

	public participateInfo = ({
		conferenceId
	}: Meeting.Conference.ParticipateInfo.Request): Promise<ReturnData<Meeting.Conference.ParticipateInfo.Response>> =>
		this.get(this.APIPath(conferenceId, "participate"));

	public participate = ({
		conferenceId,
		...rest
	}: Meeting.Conference.Participate.Request): Promise<ReturnData<Meeting.Conference.Participate.Response>> =>
		this.post(this.APIPath(conferenceId, "participate"), rest);

	public participateOk = ({
		conferenceId,
		...rest
	}: Meeting.Conference.ParticipateOk.Request): Promise<ReturnData<Meeting.Conference.ParticipateOk.Response>> =>
		this.post(this.APIPath(conferenceId, "participate-ok"), rest);

	/**
	 * -> '나가기'버튼 클릭을 통한 회의 나가기 동작은 normalLeave true, 그 외 브라우저종료 등을 통한 API 호출은 normalLeave false로 설정 필요
	 * -> normalLeave true인 경우 회의 종료 지연시간(20초) 없이 즉시 회의 종료 처리
	 */
	public leave = ({
		conferenceId,
		...rest
	}: Meeting.Conference.Leave.Request): Promise<ReturnData<Meeting.Conference.Leave.Response>> =>
		this.post(this.APIPath(conferenceId, "leave"), rest);

	public update = ({
		conferenceId,
		...rest
	}: Meeting.Conference.Update.Request): Promise<ReturnData<Meeting.Conference.Update.Response>> =>
		this.put(this.APIPath(conferenceId), rest);

	public toPublic = ({
		conferenceId,
		...rest
	}: Meeting.Conference.ToPublic.Request): Promise<ReturnData<Meeting.Conference.ToPublic.Response>> =>
		this.put(this.APIPath(conferenceId, "to-public"), rest);

	public toPrivate = ({
		conferenceId,
		...rest
	}: Meeting.Conference.ToPrivate.Request): Promise<ReturnData<Meeting.Conference.ToPrivate.Response>> =>
		this.put(this.APIPath(conferenceId, "to-private"), rest);

	public detail = ({
		conferenceId
	}: Meeting.Conference.Detail.Request): Promise<ReturnData<Meeting.Conference.Detail.Response>> =>
		this.get(this.APIPath(conferenceId));

	public participateFromSchedule = (
		parameter?: Meeting.Conference.ParticipateFromSchedule.Request
	): Promise<ReturnData<Meeting.Conference.ParticipateFromSchedule.Response>> =>
		this.post(this.APIPath("from-schedule"), parameter);
}

class ActionLog extends BaseService {
	constructor() {
		super();
	}

	private APIPath = () => `/meeting/conferences/action-logs`;

	public list = (parameter?: Meeting.ActionLog.List.Request): Promise<ReturnData<Meeting.ActionLog.List.Response>> =>
		this.get(this.APIPath(), parameter);

	public save = (parameter: Meeting.ActionLog.Save.Request): Promise<ReturnData<Meeting.ActionLog.Save.Response>> =>
		this.post(this.APIPath(), parameter);
}

class Group extends BaseService {
	constructor() {
		super();
	}

	private APIPath = () => "meeting/groups/conferences";

	public list = (parameter: Meeting.Group.List.Request): Promise<ReturnData<Meeting.Group.List.Response>> =>
		this.get(this.APIPath(), parameter);
}

class Meeting extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `meeting/meetings${path ? `/${path}` : ""}`;

	public list = (parameter: Meeting.Meeting.List.Request): Promise<ReturnData<Meeting.Meeting.List.Response>> =>
		this.get(this.APIPath(), parameter);

	public detail = (parameter: Meeting.Meeting.Detail.Request): Promise<ReturnData<Meeting.Meeting.Detail.Response>> =>
		this.get(this.APIPath("details"), parameter);

	public simple = (parameter: Meeting.Meeting.Simple.Request): Promise<ReturnData<Meeting.Meeting.Simple.Response>> =>
		this.get(this.APIPath("simple"), parameter);

	public remove = (parameter: Meeting.Meeting.Remove.Request): Promise<ReturnData<Meeting.Meeting.Remove.Response>> =>
		this.post(this.APIPath("delete"), parameter);

	public guest = (parameter: Meeting.Guest.Request): Promise<ReturnData<Meeting.Guest.Response>> =>
		this.post("api/guests/meetings", parameter);

	public existingDates = (
		parameter: Meeting.MeetingExistingDates.Request
	): Promise<ReturnData<Meeting.MeetingExistingDates.Response>> =>
		this.get(this.APIPath("existing-dates"), parameter);
}

class Schedule extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (id?: string, path?: string) =>
		`/meeting/schedules${id ? `/${id}` : ""}${path ? `/${path}` : ""}`;

	public create = (
		parameter: Meeting.Schedule.Create.Request
	): Promise<ReturnData<Meeting.Schedule.Create.Response>> => this.post(this.APIPath(), parameter);

	public unknownInvitation = ({
		id,
		...rest
	}: Meeting.Schedule.UnknownInvitation.Request): Promise<ReturnData<Meeting.Schedule.UnknownInvitation.Response>> =>
		this.post(this.APIPath(id, "invitation-unknown"), rest);

	public denyInvitation = ({
		id,
		...rest
	}: Meeting.Schedule.DenyInvitation.Request): Promise<ReturnData<Meeting.Schedule.DenyInvitation.Response>> =>
		this.post(this.APIPath(id, "invitation-deny"), rest);

	public acceptInvitation = ({
		id,
		...rest
	}: Meeting.Schedule.AcceptInvitation.Request): Promise<ReturnData<Meeting.Schedule.AcceptInvitation.Response>> =>
		this.post(this.APIPath(id, "invitation-accept"), rest);

	public update = ({
		id,
		...rest
	}: Meeting.Schedule.Update.Request): Promise<ReturnData<Meeting.Schedule.Update.Response>> =>
		this.put(this.APIPath(id), rest);

	public saveInvitationReaction = ({
		id,
		...rest
	}: Meeting.Schedule.SaveInvitationReaction.Request): Promise<
		ReturnData<Meeting.Schedule.SaveInvitationReaction.Response>
	> => this.post(this.APIPath(id, "invitations/react"), rest);
}

class ConferenceHistory extends BaseService {
	constructor() {
		super();
	}
	private APIPath = (path?: string) => `/meeting/conferences/history${path ? `/${path}` : ""}`;
	public downloadChat = (
		parameter: Meeting.ConferenceHistory.DownloadCsvChat.Request
	): Promise<ReturnData<Meeting.ConferenceHistory.DownloadCsvChat.Response>> =>
		this.get(this.APIPath("download-csv/chat"), parameter);
	public downloadActionLog = (
		parameter: Meeting.ConferenceHistory.DownloadCsvActionLog.Request
	): Promise<ReturnData<Meeting.ConferenceHistory.DownloadCsvActionLog.Response>> =>
		this.get(this.APIPath("download-csv/action-log"), parameter);
}

class ConferenceInvite extends BaseService {
	constructor() {
		super();
	}

	private APIPath = () => `/meeting/conferences/invite`;

	public invite = (
		parameter: Meeting.ConferenceInvite.Invite.Request
	): Promise<ReturnData<Meeting.ConferenceInvite.Invite.Response>> => this.post(this.APIPath(), parameter);
}

class MeetingServiceStatic {
	constructor(
		public readonly conference = new Conference(),
		public readonly actionLog = new ActionLog(),
		public readonly group = new Group(),
		public readonly meeting = new Meeting(),
		public readonly schedule = new Schedule(),
		public readonly conferenceHistory = new ConferenceHistory(),
		public readonly conferenceInvite = new ConferenceInvite()
	) {}
}

const MeetingService = new MeetingServiceStatic();
export default MeetingService;
